/**
 * Frontend mirror of functions-forms/src/kb-assistant/industryPacks.ts.
 * Keep both in sync — Cloud Functions cannot import from src/.
 */

export const INDUSTRY_IDS = [
  'homeServices',
  'legal',
  'medicalDental',
  'professionalServices',
  'ecommerce',
  'agencyMarketing',
  'financialInsurance',
  'generic',
] ;

 

export const INDUSTRY_LABELS = {
  homeServices: 'Home services',
  legal: 'Legal',
  medicalDental: 'Medical / dental',
  professionalServices: 'Professional services',
  ecommerce: 'Ecommerce',
  agencyMarketing: 'Agency / marketing',
  financialInsurance: 'Financial / insurance',
  generic: 'Other',
};

/** Restricted verticals ship a pack with guardrails and a stated notice — not a silent generic fallback. */
 

export const INDUSTRY_SUPPORT = {
  homeServices: 'supported',
  legal: 'supported',
  medicalDental: 'supported',
  professionalServices: 'supported',
  ecommerce: 'supported',
  agencyMarketing: 'supported',
  financialInsurance: 'restricted',
  generic: 'supported',
};

export const RESTRICTED_VERTICAL_NOTICE =
  'Financial and insurance is a regulated vertical. This pack exists so the operator can take intake and hand off — it will not give advice, quote products, or collect account numbers. Confirm licences before you go live.';

/** Verticals where a missed compliance gate is worse than one extra question. */
export const REGULATED_INDUSTRY_IDS = ['legal', 'medicalDental', 'financialInsurance'];

export function isRegulatedIndustryId(id) {
  return REGULATED_INDUSTRY_IDS.includes(id);
}

export const REGULATED_VERTICAL_CONFIRM_ID = 'regulated-vertical-confirm';

export const REGULATED_VERTICAL_CONFIRM_OPTIONS = [
  { id: 'no', label: 'No' },
  { id: 'financialInsurance', label: 'Yes — financial or insurance' },
  { id: 'medicalDental', label: 'Yes — medical or dental' },
  { id: 'legal', label: 'Yes — legal' },
];

 


















export const REGULATED_VERTICAL_CONFIRM_QUESTION = {
  id: REGULATED_VERTICAL_CONFIRM_ID,
  question: 'Is this a financial, medical, or legal practice?',
  why: 'Missing a licence or clinical-data gate is a far more expensive error than asking one extra question. These channels are not a licensed conversation.',
  appliesWhenMissing: 'always',
  kind: 'compliance',
  answerInput: 'text',
};

const MISSING_PRICE_PAGE = {
  id: 'missing-high-value-page',
  question:
    'We found a /pricing, /plans, or /rates page on your site that is not in the knowledge base. Should we add it?',
  why: 'A published price page that never made it into the KB is how an AI invents a number — that exact miss caused the Aug 23 "$1,500 retainer" hallucination against a real $450 floor.',
  appliesWhenMissing: 'missingHighValuePage',
};

export const CONVERSATION_GOAL_ID = 'conversation-goal';

export const CONVERSATION_GOALS = [
  { id: 'book_call', label: 'Book a call' },
  { id: 'request_quote', label: 'Request a quote' },
  { id: 'schedule_visit', label: 'Schedule a visit' },
  { id: 'purchase', label: 'Complete a purchase' },
  { id: 'qualify_then_handoff', label: 'Qualify, then hand off to a human' },
  { id: 'custom', label: 'Something else' },
] ;

 

/** Always appears with compliance. Skipping these loses the operator's spine. */
export const ESSENTIAL_ALWAYS_ASK_IDS = [
  CONVERSATION_GOAL_ID,
  'always-faq-harvest',
  'always-disqualify',
  'always-do-not',
  'always-never-say',
] ;

/** Competes for leftover slots after reserved pack preference questions. */
export const HELPFUL_ALWAYS_ASK_IDS = [
  'always-objections',
  'always-worth-time',
  'always-response-time',
  'always-handoff',
  'always-contact-routes',
  'always-hours',
  'always-after-hours',
  'always-differentiators',
] ;

export const ALWAYS_ASK_IDS = [
  ...ESSENTIAL_ALWAYS_ASK_IDS,
  ...HELPFUL_ALWAYS_ASK_IDS,
  'always-restricted-rules',
] ;

export const ALWAYS_ASK_QUESTIONS = [
  {
    id: CONVERSATION_GOAL_ID,
    question: 'What should a successful conversation end with?',
    why: 'A knowledge base of facts produces answers. A goal produces an operator that moves toward an outcome — book a call, get a quote, or hand off — instead of only being accurate.',
    appliesWhenMissing: 'always',
  },
  {
    id: 'always-faq-harvest',
    question: 'What are the three questions you get asked most, and how do you answer them?',
    why: 'This is the highest-yield question in the set — it produces knowledge-base content in your own voice, matched to real demand.',
    appliesWhenMissing: 'always',
    answerInput: 'multiline',
  },
  {
    id: 'always-disqualify',
    question: 'What disqualifies a lead outright?',
    why: 'So the AI can say no early instead of setting up a meeting you will decline.',
    appliesWhenMissing: 'always',
  },
  {
    id: 'always-do-not',
    question: 'What do you not do or not serve?',
    why: 'So the AI can tell a caller you do not cover them, instead of booking a job you will cancel.',
    appliesWhenMissing: 'always',
  },
  {
    id: 'always-objections',
    question: 'What do prospects push back on, and what is your honest answer?',
    why: 'The objection you hear on every sales call is the one the AI will face. Write the honest answer so it does not invent a softer one.',
    appliesWhenMissing: 'always',
  },
  {
    id: 'always-worth-time',
    question: 'What makes a lead worth your time?',
    why: 'So the AI can tell a serious buyer from a time-waster before anyone books a call you will cancel.',
    appliesWhenMissing: 'always',
  },
  {
    id: 'always-response-time',
    question: 'What response-time promise may the AI state?',
    why: 'So the AI does not invent "we reply in five minutes" when you answer next-day.',
    appliesWhenMissing: 'always',
  },
  {
    id: 'always-handoff',
    question: 'Who takes over from the AI, and when?',
    why: 'So the AI hands off at a real trigger — budget, urgency, a named person — instead of improvising.',
    appliesWhenMissing: 'always',
  },
  {
    id: 'always-never-say',
    question: 'What must the AI never say?',
    why: 'A phrase you never want spoken is how a lead hears a promise you cannot keep. Write the words; the AI will not invent its own list.',
    appliesWhenMissing: 'always',
  },
  {
    id: 'always-restricted-rules',
    question:
      'Are there rules or claims your industry restricts — things you must say, or must never say?',
    why: 'Fair housing, FERPA, donor-data rules, licensed-speech limits — we will not guess which apply. If you write them here, the operator will not invent a safer-sounding version.',
    appliesWhenMissing: 'always',
    kind: 'compliance',
    answerInput: 'multiline',
  },
  {
    id: 'always-contact-routes',
    question: 'How does a lead reach a human, and which route should the AI offer?',
    why: 'Phone, email, a booking link, or a named person — if this is missing the AI invents a path you do not staff.',
    appliesWhenMissing: 'contactRoutes',
  },
  {
    id: 'always-hours',
    question: 'What days and hours may the AI say you are open?',
    why: 'Buyers ask this constantly. A guessed Saturday opening is how you get a no-show on a day you are closed.',
    appliesWhenMissing: 'hours',
  },
  {
    id: 'always-after-hours',
    question: 'After hours or when you are closed, what should the AI tell them?',
    why: 'So the AI does not promise a same-night reply or visit when the next touch is Monday morning.',
    appliesWhenMissing: 'hours',
  },
  {
    id: 'always-differentiators',
    question: 'What makes you different from the next company a buyer would call?',
    why: 'Without this the AI sounds like every other operator. One proof point — years, a method, a guarantee you actually offer — is enough.',
    appliesWhenMissing: 'differentiators',
  },
];

function packQuestions(...groups) {
  return groups.flat();
}

export const INDUSTRY_PACKS = {
  homeServices: {
    id: 'homeServices',
    label: INDUSTRY_LABELS.homeServices,
    questions: packQuestions(ALWAYS_ASK_QUESTIONS, [
      MISSING_PRICE_PAGE,
      {
        id: 'home-pricing',
        question: 'Do you give a ballpark by phone, or only after a site visit?',
        why: 'Ballpark pricing sight-unseen invites disputes. The AI should only do what you actually do.',
        appliesWhenMissing: 'pricing',
      },
      {
        id: 'home-min-job',
        question: 'What is the minimum job size you will take?',
        why: 'So the AI can decline a $80 service call if your floor is a full replacement.',
        appliesWhenMissing: 'pricing',
      },
      {
        id: 'home-radius',
        question: 'What is your service radius, and is there a hard boundary you will not cross?',
        why: 'So the AI can tell a caller 40 miles away that you do not cover them, instead of booking a job you will cancel.',
        appliesWhenMissing: 'serviceArea',
      },
      {
        id: 'home-emergency',
        question: 'Do you take emergency or after-hours work, or only scheduled jobs?',
        why: 'So the AI does not promise a same-night visit when you only book next-day.',
        appliesWhenMissing: 'hours',
      },
      {
        id: 'home-brands',
        question: 'Which brands or systems will you not service?',
        why: 'So the AI can say no to a boiler you do not touch instead of booking it.',
        appliesWhenMissing: 'disqualifiers',
      },
      {
        id: 'home-permits',
        question: 'Who handles permits — you, the customer, or it depends?',
        why: 'So the AI does not imply a job is turnkey when the owner still needs a permit.',
        appliesWhenMissing: 'services',
      },
      {
        id: 'home-warranty',
        question: 'What warranty terms may the AI repeat?',
        why: 'Warranty language creates obligations. The AI should only state terms you wrote down.',
        appliesWhenMissing: 'differentiators',
      },
      {
        id: 'home-seasonal',
        question: 'Are there seasonal capacity limits the AI should know?',
        why: 'So the AI does not book August AC work when you are already two weeks out.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'home-licence',
        question: 'What licence numbers and insurance details should the AI display?',
        why: 'Contractor licence disclosure is mandatory in many states. The AI only repeats numbers you supplied.',
        appliesWhenMissing: 'always',
        kind: 'compliance',
      },
    ]),
  },

  legal: {
    id: 'legal',
    label: INDUSTRY_LABELS.legal,
    questions: packQuestions(ALWAYS_ASK_QUESTIONS, [
      MISSING_PRICE_PAGE,
      {
        id: 'legal-areas',
        question: 'Which practice areas do you take, and which do you specifically decline?',
        why: 'So the AI does not take a family-law inquiry if you only do personal injury.',
        appliesWhenMissing: 'services',
      },
      {
        id: 'legal-jurisdiction',
        question: 'In which jurisdictions are you admitted?',
        why: 'So the AI does not tell an out-of-state caller you can represent them.',
        appliesWhenMissing: 'serviceArea',
      },
      {
        id: 'legal-consult',
        question: 'Is the consultation free or paid, and how long is it?',
        why: 'This is the first question most legal leads ask after "do you handle my case."',
        appliesWhenMissing: 'pricing',
      },
      {
        id: 'legal-fee-model',
        question: 'How do you charge — contingency, hourly, or flat?',
        why: 'A made-up retainer is a liability. Only state the model you actually use.',
        appliesWhenMissing: 'pricing',
      },
      {
        id: 'legal-sol',
        question: 'What statute-of-limitation urgency should the AI flag?',
        why: 'So a late caller hears "speak to the firm today," not a casual "we will get back to you."',
        appliesWhenMissing: 'always',
      },
      {
        id: 'legal-conflict',
        question: 'What must be true before intake — conflict check, documents, a named person?',
        why: 'Conflict checks belong before substantive intake, not after the AI has already promised a consult.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'legal-intake-facts',
        question: 'What facts may intake collect, and what must it not?',
        why: 'So the AI does not take a statement that creates a confidentiality expectation you did not intend.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'legal-disclaimer',
        question: 'What advertising disclaimer must appear (bar number, "Advertisement")?',
        why: 'Some bars require labelling. The AI should only use the disclaimer you provided.',
        appliesWhenMissing: 'always',
        kind: 'compliance',
      },
      {
        id: 'legal-advice-boundary',
        question: 'Confirm: the operator does intake only and never gives legal advice. What is the handoff line?',
        why: 'Unauthorized practice of law and an implied attorney-client relationship start when the AI answers a legal question.',
        appliesWhenMissing: 'always',
        kind: 'compliance',
      },
    ]),
  },

  medicalDental: {
    id: 'medicalDental',
    label: INDUSTRY_LABELS.medicalDental,
    questions: packQuestions(ALWAYS_ASK_QUESTIONS, [
      MISSING_PRICE_PAGE,
      {
        id: 'med-services',
        question: 'Which services do you offer, and which do you refer out?',
        why: 'So the AI does not book a procedure you do not perform.',
        appliesWhenMissing: 'services',
      },
      {
        id: 'med-insurance',
        question: 'Which insurance plans do you accept, and what is the out-of-network policy?',
        why: 'Insurance is the first filter for most medical and dental callers.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'med-new-patients',
        question: 'Are you accepting new patients, and is there a wait?',
        why: 'So the AI does not book someone you will have to waitlist.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'med-ages',
        question: 'Which age ranges do you treat?',
        why: 'So the AI can say no to a pediatric visit if you only see adults.',
        appliesWhenMissing: 'disqualifiers',
      },
      {
        id: 'med-emergency',
        question: 'What should someone do if this is urgent after hours?',
        why: 'So the AI sends them to the ER or on-call number you named, not a made-up option.',
        appliesWhenMissing: 'hours',
      },
      {
        id: 'med-clinical-never',
        question:
          'Confirm: the AI must never collect symptoms, conditions, medications, or other clinical detail. When does it route to a human immediately?',
        why: 'These channels are not HIPAA-compliant. Collecting clinical data is a breach risk for you and for us.',
        appliesWhenMissing: 'always',
        kind: 'compliance',
      },
    ]),
  },

  professionalServices: {
    id: 'professionalServices',
    label: INDUSTRY_LABELS.professionalServices,
    questions: packQuestions(ALWAYS_ASK_QUESTIONS, [
      MISSING_PRICE_PAGE,
      {
        id: 'pro-engagement',
        question: 'What engagement types do you take, and what is the minimum size?',
        why: 'So the AI does not invent a minimum the way the GAM operator invented $1,500.',
        appliesWhenMissing: 'pricing',
      },
      {
        id: 'pro-industries',
        question: 'Which industries do you serve, and which do you decline?',
        why: 'So the AI can qualify out a bad-fit industry before anyone books time.',
        appliesWhenMissing: 'disqualifiers',
      },
      {
        id: 'pro-timeline',
        question: 'What deliverable timelines may the AI state?',
        why: 'So the AI does not promise next-week delivery when you are booked out a month.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'pro-fee-model',
        question: 'Is this retainer or project work, and what is the first paid step?',
        why: 'So the AI describes a real next step instead of a vague "we will talk."',
        appliesWhenMissing: 'pricing',
      },
      {
        id: 'pro-decision-maker',
        question: 'Who is usually the decision-maker on a fit call?',
        why: 'So the AI can ask for the right person instead of pitching an intern.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'pro-fit',
        question: 'What must be true for a fit call to be worth booking?',
        why: 'So the AI books qualified conversations, not curiosity calls.',
        appliesWhenMissing: 'always',
      },
    ]),
  },

  ecommerce: {
    id: 'ecommerce',
    label: INDUSTRY_LABELS.ecommerce,
    questions: packQuestions(ALWAYS_ASK_QUESTIONS, [
      MISSING_PRICE_PAGE,
      {
        id: 'ecom-categories',
        question: 'What categories do you sell, and what is the out-of-stock policy?',
        why: 'So the AI does not take an order you cannot fulfill.',
        appliesWhenMissing: 'services',
      },
      {
        id: 'ecom-shipping',
        question: 'Where do you ship, what does it cost, and how long does it take?',
        why: 'Shipping promises are enforceable. The AI should only repeat what you published.',
        appliesWhenMissing: 'serviceArea',
      },
      {
        id: 'ecom-order-status',
        question: 'What may the AI say about order status and tracking, and what must it escalate to a human?',
        why: '"Where is my order" is a large share of ecommerce inquiries. A guessed tracking number is worse than a handoff.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'ecom-returns',
        question: 'What is the return and warranty policy in one sentence?',
        why: 'This is the objection that kills the order if the AI guesses.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'ecom-wholesale',
        question: 'Do you handle wholesale or bulk, or only direct-to-consumer?',
        why: 'So the AI does not quote retail terms to a buyer who needs a catalog.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'ecom-escalate',
        question: 'Which sizing or compatibility questions may the AI answer, and what must it escalate?',
        why: 'A wrong size or fit answer becomes a return. Escalate what you have not written down.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'ecom-promo',
        question: 'What is the promo-code policy — may the AI invent or extend a code?',
        why: 'The AI must never invent a discount. Only codes you named.',
        appliesWhenMissing: 'pricing',
      },
    ]),
  },

  agencyMarketing: {
    id: 'agencyMarketing',
    label: INDUSTRY_LABELS.agencyMarketing,
    questions: packQuestions(ALWAYS_ASK_QUESTIONS, [
      MISSING_PRICE_PAGE,
      {
        id: 'agency-pricing',
        question: 'What is the real floor, and where does pricing live — retainer, project, or a page?',
        why: 'Inventing a retainer is exactly how GAM quoted $1,500 against a $450 floor.',
        appliesWhenMissing: 'pricing',
      },
      {
        id: 'agency-packages',
        question: 'Which services and packages do you sell?',
        why: 'So the AI does not pitch a service you sunset.',
        appliesWhenMissing: 'services',
      },
      {
        id: 'agency-term',
        question: 'What is the contract length, and how does cancellation work?',
        why: 'This is the second question after price, and guessing it is how you lose trust.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'agency-reporting',
        question: 'What reporting cadence may the AI promise?',
        why: 'So the AI does not promise a weekly dashboard you do not run.',
        appliesWhenMissing: 'always',
      },
      {
        id: 'agency-decline',
        question: 'Which industries or client types do you decline?',
        why: 'So the AI can decline a $200/mo lead instead of booking a call you will cancel.',
        appliesWhenMissing: 'disqualifiers',
      },
      {
        id: 'agency-onboarding',
        question: 'What does onboarding look like, and how long is month one?',
        why: 'So the AI can describe a real start instead of "we will make a plan."',
        appliesWhenMissing: 'always',
      },
      {
        id: 'agency-no-guarantee',
        question: 'What results will you not promise — rankings, revenue, leads?',
        why: 'Guaranteed rankings or results are an FTC and platform-policy problem. The operator must never make them.',
        appliesWhenMissing: 'always',
        kind: 'compliance',
      },
    ]),
  },

  financialInsurance: {
    id: 'financialInsurance',
    label: INDUSTRY_LABELS.financialInsurance,
    questions: packQuestions(ALWAYS_ASK_QUESTIONS, [
      MISSING_PRICE_PAGE,
      {
        id: 'fin-intake',
        question: 'What intake may the AI take — name, callback, a licensed-rep booking — and nothing else?',
        why: 'This channel is not a licensed conversation. Intake only, then a human with the right licence.',
        appliesWhenMissing: 'services',
      },
      {
        id: 'fin-states',
        question: 'In which states are you licensed to operate?',
        why: 'Licensing is per state. The AI must not imply you can help a caller you are not licensed for.',
        appliesWhenMissing: 'serviceArea',
      },
      {
        id: 'fin-products',
        question: 'Which products may be mentioned as offered, without quoting or recommending?',
        why: 'Naming a product you sell is not advice. Recommending or quoting one is.',
        appliesWhenMissing: 'services',
      },
      {
        id: 'fin-licence',
        question: 'What licence numbers and firm registrations should the AI display?',
        why: 'Licence disclosure is required in most states. The AI only repeats numbers you supplied.',
        appliesWhenMissing: 'always',
        kind: 'compliance',
      },
      {
        id: 'fin-no-advice',
        question:
          'Confirm: the operator never gives financial or insurance advice, never quotes a policy, and never collects account or SSN data. What is the handoff line?',
        why: 'Advice without a licence is a regulated gap. This pack exists so we can decline that path by default, not invent coverage.',
        appliesWhenMissing: 'always',
        kind: 'compliance',
      },
    ]),
  },

  generic: {
    id: 'generic',
    label: INDUSTRY_LABELS.generic,
    questions: packQuestions(ALWAYS_ASK_QUESTIONS, [
      MISSING_PRICE_PAGE,
      {
        id: 'generic-pricing',
        question: 'How do you price, and may the AI state numbers that appear in the KB?',
        why: 'If the KB has no prices, the AI will invent them unless you say how to handle that.',
        appliesWhenMissing: 'pricing',
      },
      {
        id: 'generic-services',
        question: 'What do you sell — the services or products a buyer should hear first?',
        why: 'So the AI describes your offer instead of guessing from the homepage headline.',
        appliesWhenMissing: 'services',
      },
      {
        id: 'generic-area',
        question: 'Where do you serve customers, and where do you not?',
        why: 'So the AI can tell a caller outside your area that you do not cover them.',
        appliesWhenMissing: 'serviceArea',
      },
    ]),
  },
};

export function getIndustryPack(industry) {
  return INDUSTRY_PACKS[industry] || INDUSTRY_PACKS.generic;
}

export function isAlwaysAskId(id) {
  return (ALWAYS_ASK_IDS ).includes(id);
}

export function isEssentialAlwaysAskId(id) {
  return (ESSENTIAL_ALWAYS_ASK_IDS ).includes(id);
}

export function isHelpfulAlwaysAskId(id) {
  return (HELPFUL_ALWAYS_ASK_IDS ).includes(id);
}

export function questionKind(question) {
  return question.kind || 'preference';
}

export function isSettableQuestion(question) {
  return questionKind(question) !== 'hardRule';
}
